/**
 * Connections (ADR-029 §2) — a workspace's connected outside accounts, for the
 * host's connect sheet and Workspace Controls. Server state through React
 * Query; every write invalidates the workspace's list.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useConnectionProviders() {
  return useQuery({
    queryKey: queryKeys.connections.providers(),
    queryFn: async () => (await api.listConnectionProviders()).providers,
    staleTime: 60 * 60 * 1000,
  });
}

export function useHolderConnections(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.connections.forHolder(holderId ?? ''),
    queryFn: () => api.listConnections(holderId!),
    enabled: holderId !== null,
  });
}

function useInvalidateHolderConnections(holderId: string | null) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.connections.forHolder(holderId ?? '') });
}

export function useGrantConnection(holderId: string | null) {
  const invalidate = useInvalidateHolderConnections(holderId);
  return useMutation({
    mutationFn: (input: { connectionId: string; archeId: string }) =>
      api.grantConnection(input.connectionId, input.archeId),
    onSuccess: invalidate,
  });
}

export function useRevokeConnectionGrant(holderId: string | null) {
  const invalidate = useInvalidateHolderConnections(holderId);
  return useMutation({
    mutationFn: (input: { connectionId: string; archeId: string }) =>
      api.revokeConnectionGrant(input.connectionId, input.archeId),
    onSuccess: invalidate,
  });
}

export function useDisconnectConnection(holderId: string | null) {
  const invalidate = useInvalidateHolderConnections(holderId);
  return useMutation({
    mutationFn: (connectionId: string) => api.disconnectConnection(connectionId),
    onSuccess: invalidate,
  });
}
